import React, { HTMLAttributes, PropsWithChildren, KeyboardEventHandler, useRef } from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { useLineTabContext } from './LineTabs';
import { colors, semantic_colors, text_styles } from '../../foundation';
import { TabWidth } from './LineTabs';
import { IconBeta, IconCircleFill, IconCircleQuestion, IconNew } from '../icons/generated';
import { Badge } from '../badge';
import { Popover, PopoverProps } from '../popover';

export interface LineTabProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  id: string;
  /** @default 'medium' */
  size?: 'medium' | 'large';
  /** 새로운 기능 배지 표시 여부 */
  hasNewBadge?: boolean;
  /** 베타 기능 배지 표시 여부 */
  hasBetaBadge?: boolean;
  /** 알림 표시 여부 */
  hasNotification?: boolean;
  /** 탭에 표시할 개수 */
  count?: number;
  popover?: Pick<PopoverProps, 'description' | 'title'>;
  /** 탭 선택 이벤트 */
  onTabSelect?: (id: string) => void;
}

export type LineTabComponentProps = PropsWithChildren<LineTabProps>;

export const LineTab = ({
  id,
  size = 'medium',
  onTabSelect,
  className,
  children,
  hasNewBadge,
  hasBetaBadge,
  hasNotification,
  count,
  popover,
  ...props
}: LineTabComponentProps) => {
  const { activeTabId, onChange, width, paddingTop = true } = useLineTabContext();
  const tabRef = useRef<HTMLDivElement | null>(null);
  const selected = activeTabId === id;

  const handleClick = (id: string) => {
    onTabSelect?.(id);
    onChange?.(id);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!tabRef.current) {
      return;
    }
    const currentFocus = tabRef.current.ownerDocument.activeElement as HTMLElement;

    if (currentFocus.getAttribute('role') !== 'tab') {
      return;
    }

    if (e.key === 'Enter') {
      const target = e.target as HTMLElement;
      target.id !== activeTabId && handleClick(target.id);
    }
  };

  return (
    <TabItemWrapper
      className={classNames('pds-line-tab', className)}
      role='tab'
      $width={width}
      $paddingTop={paddingTop}
      aria-selected={selected}
      tabIndex={0}
      onClick={() => handleClick(id)}
      onKeyDown={handleKeyDown}
      ref={tabRef}
      id={id}
      {...props}
    >
      <TabItem selected={selected} size={size} paddingTop={paddingTop}>
        {children}
        {hasNewBadge && <IconNew color={semantic_colors.brand.primary} size={8} />}
        {hasBetaBadge && <IconBeta color={colors.light_blue500} size={21} />}
        {hasNotification && (
          <CircleAlignContainer>
            <IconCircleFill color={semantic_colors.brand.primary} size={4} />
          </CircleAlignContainer>
        )}
        {popover && (
          <Popover placement='bottom' title={popover.title} description={popover.description}>
            <QuestionContainer>
              <IconCircleQuestion color={semantic_colors.content.tertiary} />
            </QuestionContainer>
          </Popover>
        )}
        {typeof count === 'number' && (
          <StyledBadge variant='filled' shape='pill' color='gray' size='small'>
            {count.toLocaleString()}
          </StyledBadge>
        )}
      </TabItem>
      {selected && <Indicator initial={{ y: '0px' }} transition={{ duration: 0.23 }} layoutId='activeIndicator' />}
    </TabItemWrapper>
  );
};

const Indicator = styled(motion.div)`
  height: 2px;
  background-color: ${semantic_colors.content.primary};
`;

const StyledBadge = styled(Badge)`
  margin-left: 2px;
`;

const CircleAlignContainer = styled.span`
  display: inline-flex;
  padding-bottom: 12px;
`;
const QuestionContainer = styled.span`
  display: inline-flex;
  margin-left: 2px;
`;

const TabItem = styled.div<{ selected: boolean; size?: 'medium' | 'large'; paddingTop: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ paddingTop }) => (paddingTop ? '48px' : '36px')};
  box-sizing: border-box;
  min-height: 26px;
  padding-top: ${({ paddingTop }) => (paddingTop ? '12px' : '0')};
  padding-bottom: 10px;
  gap: 2px;
  ${({ size }) => (size === 'medium' ? text_styles.Body_15_SemiBold : text_styles.Heading_17_SemiBold)};
  color: ${({ selected }) => (selected ? semantic_colors.content.primary : semantic_colors.content.tertiary)};
  text-align: center;
  white-space: nowrap;
  transition: all 0.1s linear;

  &:hover {
    color: ${semantic_colors.content.primary};
  }
`;

export const TabItemWrapper = styled.div<{ $width?: TabWidth; $paddingTop: boolean }>`
  height: ${({ $paddingTop }) => ($paddingTop ? '50px' : '38px')};
  cursor: pointer;
  ${({ $width }) =>
    $width === 'fill'
      ? `
          flex: 1 0 0px;
        `
      : `
          flex: 0 0 auto;
          width: auto;
       `};

  &:focus {
    outline: 3px solid ${semantic_colors.tab_focus};
    outline-offset: -3px;
    box-shadow: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;
